<template>
  <div class="min-h-screen bg-gray-50">
    <header class="bg-white shadow-sm">
      <div class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <img class="h-8" src="/logo-mifo.svg" alt="MIFO" />
        <div class="text-sm text-gray-500">Produkt-Checkout</div>
      </div>
    </header>

    <div class="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
      <div class="space-y-6">
        <div class="card">
          <h2 class="text-xl font-heading font-semibold text-mifo-primary mb-6">Produkt</h2>
          <div v-if="product">
            <h3 class="text-lg font-semibold">{{ product.name }}</h3>
            <div class="prose max-w-none mt-2" v-html="product.descriptionHtml"></div>
          </div>
        </div>

        <div class="card">
          <h2 class="text-xl font-heading font-semibold text-mifo-primary mb-6">Ihre Daten</h2>
          <CustomerAddressForm v-model="customer" :required="true" :showMedicalFields="false" />
        </div>

        <div class="card" v-if="product">
          <h2 class="text-xl font-heading font-semibold text-mifo-primary mb-4">Produkt</h2>
          <div class="border-2 rounded-lg p-4 border-mifo-accent bg-mifo-accent/5">
            <div class="flex items-center justify-between">
              <div class="flex-1 pr-4">
                <h3 class="font-medium text-gray-900">{{ product.name }}</h3>
                <div class="text-sm text-gray-600 mt-1" v-html="product.descriptionHtml"></div>
              </div>
              <div class="text-right">
                <p class="text-lg font-semibold text-mifo-primary">{{ formatCurrency(product.priceCents || 0) }}</p>
              </div>
            </div>

            <div class="mt-4" v-if="product.type === 'multiple'">
              <label class="block text-sm font-medium text-gray-700 mb-1">Anzahl</label>
              <QuantitySelector 
                v-model="quantity" 
                :min-value="1" 
              />
            </div>
            <div class="mt-2" v-else>
              <p class="text-sm text-gray-500">Dieses Produkt wird automatisch ausgewählt.</p>
            </div>
          </div>
        </div>

        <div class="card">
          <div class="flex items-center justify-between mb-2">
            <span class="text-gray-600">Zwischensumme</span>
            <span class="font-semibold">{{ formatCurrency(subtotal) }}</span>
          </div>
          <div class="flex items-center justify-between mb-2" v-if="sendingCosts > 0">
            <span class="text-gray-600">Versandkosten</span>
            <span class="font-semibold">{{ formatCurrency(sendingCosts) }}</span>
          </div>
          <div class="flex items-center justify-between">
            <span class="text-gray-600">Gesamt</span>
            <span class="font-semibold text-mifo-primary">{{ formatCurrency(total) }}</span>
          </div>
        </div>

        <div class="card">
          <h2 class="text-xl font-heading font-semibold text-mifo-primary mb-6">Zahlungsmethode wählen</h2>
          <PaymentMethodSelector v-model="paymentMethod" />
        </div>

        <LegalNotice />

        <div class="flex justify-end">
          <button class="btn-primary w-full" :disabled="!valid || !paymentMethod" @click="checkout">Sicher bezahlen</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import api from '@/utils/axios'
import { formatCurrency } from 'shared/utils'
import { FORM_FIELDS } from 'shared/constants'
import PaymentMethodSelector from '@/components/PaymentMethodSelector.vue'
import LegalNotice from '@/components/LegalNotice.vue'
import CustomerAddressForm from '@/components/CustomerAddressForm.vue'
import QuantitySelector from '@/components/QuantitySelector.vue'

export default {
  name: 'ProductCheckout',
  components: { PaymentMethodSelector, LegalNotice, CustomerAddressForm, QuantitySelector },
  setup() {
    const route = useRoute()
    const product = ref(null)
    const selected = ref(false)
    const quantity = ref(1)
    const paymentMethod = ref(null)
    const customer = ref({ anrede: '', titel: '', firstName: '', lastName: '', street: '', houseNumber: '', postalCode: '', city: '', country: 'DE', email: '' })
    const anredeOptions = FORM_FIELDS.ANREDE_OPTIONS

    const load = async () => {
      const { data } = await api.get(`/products/${route.params.id}`)
      product.value = data
      // Auto-select for single products, multiple products are always selected
      if (product.value?.type === 'single') {
        selected.value = true
        quantity.value = 1
      } else if (product.value?.type === 'multiple') {
        // For multiple products, always selected with default quantity 1
        selected.value = true
        quantity.value = 1
      }
    }

    const subtotal = computed(() => {
      if (!product.value) return 0
      const qty = product.value.type === 'multiple' ? Math.max(1, parseInt(quantity.value || 1, 10)) : 1
      return (product.value.priceCents || 0) * qty
    })
    const sendingCosts = computed(() => {
      if (!product.value) return 0
      return product.value.sendingCostsCents || 0
    })
    const total = computed(() => subtotal.value + sendingCosts.value)
    const valid = computed(() => !!product.value && customer.value.firstName && customer.value.lastName && customer.value.email && customer.value.street && customer.value.postalCode && customer.value.city && customer.value.anrede)

    const checkout = async () => {
      const payload = {
        type: 'product',
        productId: product.value.id,
        productQuantity: product.value.type === 'multiple' ? Math.max(1, parseInt(quantity.value || 1, 10)) : 1,
        customer: customer.value,
        paymentMethod: paymentMethod.value || 'creditcard'
      }
      const { data } = await api.post('/orders', payload)
      if (data.paymentUrl) window.location.href = data.paymentUrl
    }

    onMounted(load)
    return { product, selected, quantity, customer, paymentMethod, anredeOptions, subtotal, sendingCosts, total, valid, checkout, formatCurrency }
  }
}
</script>

